import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { recoverPasswordAction, setSuccessFalse } from "../../store/actions";
import { fireAnalytics } from "../../utils/firebase.js";
import { ArrowLeft } from "lucide-react";
import styles from "./ForgotPasswordNew.module.scss";
import LogoEdify from "../../assets/images/Logo-edify-no-bg.png";
import { useNavigate } from "react-router-dom";

interface ForgotPasswordNewProps {
  success: boolean;
  recoverPasswordAction: (values: any) => void;
  setSuccessFalse: () => void;
}

interface ForgotPasswordFormValues {
  email: string;
}

const ForgotPasswordNew: React.FC<ForgotPasswordNewProps> = (props) => {
  const [formData, setFormData] = useState<ForgotPasswordFormValues>({
    email: "",
  });
  const [errors, setErrors] = useState<Partial<ForgotPasswordFormValues>>({});
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fireAnalytics('password_recovery', { category: 'navigation'});
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Partial<ForgotPasswordFormValues> = {};

    if (!formData.email) {
      newErrors.email = "Escribe tu e-mail";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Debes ingresar un formato de email válido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof ForgotPasswordFormValues, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      props.recoverPasswordAction(formData);
      fireAnalytics('recover_password', { category: 'authentication'});
    } catch (error) {
      console.error("Recovery error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    props.setSuccessFalse();
    navigate("/");
  };

  if (props.success) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.successContainer}>
            <motion.div
              className={styles.successIcon}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
            >
              ✓
            </motion.div>
            <motion.h1
              className={styles.successTitle}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              ¡Listo!
            </motion.h1>
            <motion.p
              className={styles.successMessage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Te hemos enviado un mail con las instrucciones para recuperar tu contraseña.
            </motion.p>
            <motion.button
              className={styles.backButton}
              onClick={handleBack}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              Ir al login
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <motion.img
            src={LogoEdify}
            alt="Edify Logo"
            className={styles.logo}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          />
          <motion.h1
            className={styles.mainTitle}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            ¿Olvidaste tu contraseña?
          </motion.h1>
          <motion.p
            className={styles.mainSubtitle}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            No te preocupes, envíanos tu mail para que puedas iniciar el proceso de recuperación.
          </motion.p>
        </div>

        <div className={styles.formContainer}>
          <motion.form
            className={`${styles.form} ${styles.animationContainer}`}
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="email">E-mail</label>
              <input
                type="email"
                placeholder="tu@email.com"
                className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit(e as any);
                  }
                }}
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
            </div>

            <button
              type="submit"
              disabled={isLoading || !formData.email}
              className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
            >
              {isLoading ? 'Enviando...' : 'Recuperar Contraseña'}
            </button>
          </motion.form>

          <motion.div
            className={styles.goBack}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Link to="/" className={styles.goBackLink}>
              <ArrowLeft size={16} />
              <span>Volver al login</span>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    success: state.general.success,
  };
};

export default connect(mapStateToProps, {
  recoverPasswordAction,
  setSuccessFalse,
})(ForgotPasswordNew);
